import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import Loader from '../../../components/common/Loader';
import { GLOBAL_DEFAULT } from '../../../constants/global';
import { orderService } from '../services';
import { applyOrdersArchiveRecipientWarehouse, toOrdersArray } from './thermal/orderPrintNormalize';
import PaginatedPackingSlipOrder from './thermal/PaginatedPackingSlipOrder';
import './thermal/printPackingSlipPeel.scss';
import { useThermalPrintViewSetup } from './thermal/useThermalPrintViewSetup';

/** PNG URLs for carrier label(s), one sheet per URL when printing. */
function getCarrierLabelPngUrls(order: any): string[] {
    if (!order) {
        return [];
    }
    if (order.is_shipping_label && order?.shipping?.packages?.length) {
        return order.shipping.packages
            .map((packageItem: any) => packageItem?.label_download?.png)
            .filter(Boolean);
    }
    const single = order?.shipping?.label_download?.png;
    return single ? [single] : [];
}

const PrintShippingLabel: React.FC<any> = () => {
    const [state, setState] = useState<any>(null);
    const [orderDetail, setOrderDetail] = useState<any>();
    const [loading, setLoading] = useState<boolean>(GLOBAL_DEFAULT.LOADER_STATE);
    const [readyCount, setReadyCount] = useState<number>(0);

    useEffect(() => {
        try {
            const windowName = window.name;
            const state = JSON.parse(windowName);

            setState(state);
        } catch (error) {
            console.error('Failed to parse state from window name', error);
            window.location.href = '/app/dashboard';
        }
    }, []);

    useThermalPrintViewSetup();

    const getOrderDetail = useCallback(async () => {
        if (state?.orderId) {
            setLoading(true);
            try {
                const payload = {
                    orderIds: state?.orderId,
                    type: state?.orderType || 'Orders',
                };
                const response = await orderService.printPackingSlip(payload);

                if (state?.orderType === 'OrdersArchive') {
                    applyOrdersArchiveRecipientWarehouse(response?.data);
                }

                const raw = response?.data;
                if (raw == null) {
                    setOrderDetail(undefined);
                    setLoading(false);
                    return;
                }

                const list = Array.isArray(raw) ? raw : [raw];
                const enriched = list.map((order: any) => ({
                    ...order,
                    is_shipping_label: order?.shipping?.packages?.some(
                        (packageItem: any) => packageItem?.label_download?.png
                    ),
                }));

                setOrderDetail(Array.isArray(raw) ? enriched : enriched[0]);
                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch order detail', error);
                setLoading(false);
            }
        }
    }, [state?.orderId, state?.orderType]);

    useEffect(() => {
        getOrderDetail();
    }, [getOrderDetail]);

    useEffect(() => {
        setReadyCount(0);
    }, [orderDetail]);

    const handleOrderReady = useCallback(() => {
        setReadyCount((count) => count + 1);
    }, []);

    const orders = toOrdersArray(orderDetail);
    const hasLabels = orders.some((order: any) => getCarrierLabelPngUrls(order).length > 0);

    const printLabelOnly = () => {
        document.documentElement.classList.add('print-label-only');
        window.print();
        document.documentElement.classList.remove('print-label-only');
    };

    return (
        <>
            <Loader isLoading={loading} />
            <div className="text-center hide-on-print py-3">
                <Button
                    className="custom-min-width-85"
                    size="sm"
                    variant="dark"
                    onClick={() => window.print()}
                >
                    Print
                </Button>
                {hasLabels && (
                    <Button
                        className="custom-min-width-85 ms-2"
                        size="sm"
                        variant="dark"
                        onClick={printLabelOnly}
                    >
                        Print Label
                    </Button>
                )}
            </div>
            {orders.length > 1 && readyCount < orders.length && (
                <div className="hide-on-print py-2 start-0 position-sticky top-0 z-1 text-center">
                    <Badge bg="secondary" className="fw-normal">
                        Preparing Slips... {readyCount} of {orders.length} ready
                    </Badge>
                </div>
            )}
            {orders.map((order: any, index: number) => {
                const labelUrls = getCarrierLabelPngUrls(order);
                const orderKey = order?.order_number ?? `o-${index}`;
                return (
                    <React.Fragment key={orderKey}>
                        {labelUrls.map((src, labelIndex) => (
                            <div
                                key={`${orderKey}-carrier-${labelIndex}`}
                                className="packing-slip-peel-page packing-slip-peel-page--carrier"
                                style={{ lineHeight: 1.2 }}
                            >
                                <div className="packing-slip-carrier-inner">
                                    <img
                                        src={src}
                                        alt="Shipping Label"
                                        className="packing-slip-carrier-img"
                                    />
                                </div>
                            </div>
                        ))}
                        <PaginatedPackingSlipOrder order={order} onReady={handleOrderReady} />
                    </React.Fragment>
                );
            })}
        </>
    );
};

export default PrintShippingLabel;
